/**
 * ClientsHome — the CLIENTS LANDING page (brief of record: "Design
 * Rulings of Record — Kith CM surfaces", 2026-07-06 + mockup v5; R9
 * founder live-walk ruling: "Your day" IS the Clients landing — one
 * section, one door). Task-first, not feed-first — three bands:
 *
 *   1 Needs your attention — derived from the pipeline states of the
 *     CM's OWN notes, never hand-kept (NO create-task button, ever).
 *   2 Your clients — the caseload, the record front door, + Case Note
 *     one tap away.
 *   3 From your team — awareness of what OTHERS did, third on purpose.
 *
 * Every count lives in ONE place: the hero's purpose line carries the
 * day's visits, and each band's heading carries its own count as a quiet
 * chip. Nothing on the page says a count twice.
 *
 * Route: /t/:tenantSlug/clients (the Clients nav pill's landing; R9).
 * The full roster lives behind "All clients →" at /clients/all; the old
 * /home address redirects here. Gate: read_clinical_record — all six
 * roles hold it; during the soak the record LINKS respect the record
 * page's own view_all gate (supervising CMs test the home now;
 * primary_cm joins at rollout automatically). Access is consumed as a
 * PROP inside PageShell, so the early-return sits BELOW all hooks (the
 * locked UX-8 contract; React #310).
 *
 * Header rulings: ONE primary + Case Note (hero size; R8 client picker,
 * defaulting to today's nearest visit else last-touched); the VISIBLE
 * search bar (founder R-⌘K ruling: the bar stays, the ⌘K chip retires;
 * NO keyboard glyph anywhere on a care surface — the binding stays
 * silently wired underneath).
 *
 * Founder walk (production screenshots):
 *   - the band is the FULL-BLEED SectionBand primitive like every other
 *     page (it had shipped as an inset rounded card);
 *   - header: eyebrow CLIENTS, serif title "Your day" (the page's
 *     identity, second person per R3/R9), and the QUIET purpose line
 *     carrying the date and the day's visits ("Monday, July 6 · 2 visits
 *     today") — the DL1 anatomy every other band uses. The caseload
 *     count lives on the "Your clients" heading, never up here;
 *   - the search covers ANY client in the CM's unit: caseload plus the
 *     snapshot's unit_clients (names/navigation only). No unit set →
 *     unit_clients is empty and the search is caseload-scoped as before.
 *     For a viewer who reads every client without view_workspace (a line
 *     clinician under the tenant's Everyone on staff setting) the search
 *     and the + Case Note picker also reach every other client, from the
 *     roster read; her own lists (Your clients, her visits, her
 *     kickbacks) stay hers.
 *
 * ONE fetch (useCmHome) = one audited read server-side. All ordering
 * policy lives in src/lib/cmHome.ts (pure, unit-tested).
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import {
  canReadClinicalRecord,
  canReadEveryClient,
  canViewAnyClientRoster,
  canViewWorkspace,
  canWriteClinicalNotes,
  canWriteClinicalRecord,
  canLogInternalTime,
  canEditClientSchedule,
  canViewStaffTools,
  canManageOwnTasks,
  canManageWorkItems,
} from '@/lib/capabilities';
import { HubMyTasks } from '@/components/hub/HubMyTasks';
import { WorkItemDrawer } from '@/components/workspace/WorkItemDrawer';
import { useCmHome } from '@/lib/cmHomeApi';
import { useClientsList } from '@/lib/clientsApi';
import { orientationDate, visitsTodayCount } from '@/lib/cmHome';
import { practiceTodayIso } from '@/lib/practiceDate';
import { isSpiceOnlyViewer } from '@/lib/spiceOnlyViewer';
import { PAGE_GUTTER, PAGE_WIDTH_CLASSES, PageShell } from '@/components/primitives/PageShell';
import { SectionBand } from '@/components/primitives/SectionBand';
import { Button, buttonClassName } from '@/components/primitives/Button';
import { SearchField } from '@/components/primitives/SearchField';
import { AttentionBand } from '@/components/home/AttentionBand';
import { MyClientsList } from '@/components/home/MyClientsList';
import { MyWeekBand } from '@/components/home/MyWeekBand';
import { ComingUpList } from '@/components/home/ComingUpList';
import { TeamRail } from '@/components/home/TeamRail';
import { CaseNotePicker } from '@/components/home/CaseNotePicker';
import { VisitOrAppointmentDialog } from '@/components/clientRecord/VisitOrAppointmentDialog';
import { LogInternalTimeDialog } from '@/components/time/LogInternalTimeDialog';
import { Dialog } from '@/components/primitives/Dialog';
import { useMyHours } from '@/lib/timeEntriesApi';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useSupportContact } from '@/hooks/useSupportContact';
import { readHoursIncrement } from '@/lib/hoursIncrement';
import { pageReadText } from '@/lib/errorCopy';
import { clientCallName } from '@/lib/clientCallName';
import { NavArrowLink } from '@/components/home/NavArrowLink';
import { MY_CLIENTS_HEADINGS } from '@/lib/headingCase';
import { countPhrase } from '@/lib/myClients';
import { useWorkItems } from '@/lib/workspace';
import { isOverdue } from '@/components/workspace/workspaceFormat';

export function ClientsHome(): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const { whoami } = useTenant();

  // All hooks run BEFORE the gate is consumed inside PageShell (#310).
  const allowed = canReadClinicalRecord(whoami);
  // LP-2: the personal to-do list lives here, under
  // the attention band, for every seat that may act on tasks (the office,
  // or an own-tasks holder). It never rides the clinical gate: a seat that
  // may act on tasks opens Your day for the section alone, with no
  // clinical read. The drawer opens on ?item=, as on the Workspace.
  const canActOnTasks = canManageWorkItems(whoami) || canManageOwnTasks(whoami);
  // The counts line's overdue tasks: the agenda's own read (the same query
  // key HubMyTasks issues, so one request serves both).
  const tasksQ = useWorkItems(tenantSlug, { for_user: whoami?.user_id ?? '', limit: 100, sort: 'due_date' }, { enabled: canActOnTasks && !!whoami?.user_id });
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedItemId = searchParams.get('item');
  const closeItem = (): void => {
    const sp = new URLSearchParams(searchParams);
    sp.delete('item');
    setSearchParams(sp, { replace: true });
  };
  // "All clients" (ClientsBand) and "All SPICE clients" (MyWeekBand) open
  // the roster page, so both render for exactly the viewers that page
  // admits: every client (canReadEveryClient), or the engagement lane with
  // a roster flag.
  const showAllClientsLink = canViewAnyClientRoster(whoami);
  // Board-observer split (0152): the home page OPENS on read_clinical_record,
  // but the "+ Case Note" write-intent CTAs (the hero picker + the per-row
  // buttons) gate on write_clinical_notes — a read-only viewer sees the
  // caseload but no note doors, so the first screen a board member lands on
  // is clean, no error-on-click.
  const canWriteClinical = canWriteClinicalNotes(whoami);
  // A row's "+ Visit" plans a care manager's visit: a structured-record
  // write the server keeps to a seat that keeps the record
  // (assertKeepsClinicalRecord, LP-48), so it follows
  // canWriteClinicalRecord, not the note doors' write_clinical_notes. A
  // SPICE-only therapist keeps her note door and has no Visit.
  const canPlanVisits = canWriteClinicalRecord(whoami);
  // My week's two quiet header links and its Edit schedule: the Team page's
  // floor gate, and manage_client_identity for changing a schedule.
  const showSpiceWeekGridLink = canViewStaffTools(whoami);
  const canEditSchedule = canEditClientSchedule(whoami);
  // Internal time: the + Internal Time button opens the Log internal time
  // dialog here, My time's own (LP-43), for every viewer who may log it; a
  // read-only seat does not get the button.
  const canLogTime = canLogInternalTime(whoami);
  // One clock for the page: the date line, Coming Up, visits, the rail and
  // the overdue count (isOverdue) all read the practice's day, so the page
  // agrees with itself at every hour on every device.
  const todayIso = useMemo(() => practiceTodayIso(), []);
  const { home, isLoading, isError, error, refetch } = useCmHome(tenantSlug, todayIso, {
    enabled: allowed,
  });
  // Your day's two client lookups (the search and the + Case Note picker)
  // reach every client for a viewer who reads every client without
  // view_workspace, the Log as case note picker's rule: she can open and
  // write a note on any client, so the lookups list them. Without it
  // neither fires GET /clients. An office seat keeps today's Your day.
  const usesEveryClient = !canViewWorkspace(whoami) && canReadEveryClient(whoami);
  const roster = useClientsList(tenantSlug, { enabled: allowed && usesEveryClient });
  const moreClients = useMemo(
    () => (usesEveryClient ? roster.clients.map((c) => ({ id: c.id, display_name: c.display_name })) : []),
    [usesEveryClient, roster.clients],
  );

  const [pickerOpen, setPickerOpen] = useState(false);
  // LP-43: + Internal Time. The dialog needs the current pay period (the
  // same my-hours read My time opens on, one cache entry) and the hours
  // step; both are read once the person first asks, never on every visit.
  // The dialog opens once the period is in and the tenant's step has been
  // read (or failed to read, when the default step stands, as on My time);
  // the button shows it is working until then. A read that fails ends the
  // ask and says so; a later background refetch never opens the dialog on
  // its own. After a save she stays here and a quiet line says so.
  const [logAsked, setLogAsked] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logFailed, setLogFailed] = useState(false);
  const [logged, setLogged] = useState(false);
  const errorsAtOpenRef = useRef(0);
  const hoursQ = useMyHours(tenantSlug, null, { enabled: canLogTime && logAsked });
  const tenantConfigQ = useTenantConfig(tenantSlug, { enabled: canLogTime && logAsked });
  const supportContact = useSupportContact({ enabled: canLogTime && logAsked });
  const logReady = hoursQ.data !== null && tenantConfigQ.isFetched;
  const hoursErrors = hoursQ.errorUpdateCount;
  useEffect(() => {
    if (logOpen && !logReady && hoursErrors > errorsAtOpenRef.current) {
      setLogOpen(false);
      setLogFailed(true);
    }
  }, [logOpen, logReady, hoursErrors]);
  function openLog(): void {
    setLogged(false);
    setLogFailed(false);
    errorsAtOpenRef.current = hoursQ.errorUpdateCount;
    setLogAsked(true);
    setLogOpen(true);
    if (hoursQ.isError) hoursQ.refetch();
  }
  // "+ Visit" lives on each client row (LP-43: the header's went); the row
  // knows its own client.
  const [visitClientId, setVisitClientId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // R-⌘K: the keybinding stays silently wired — nothing on screen
  // advertises it (no chip, no glyph, no footer hint).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const clients = home?.clients ?? [];
  // The visit dialog's title calls the client by name: the row's own.
  const visitClient = visitClientId === null ? undefined : clients.find((c) => c.id === visitClientId);
  const visitClientName =
    visitClient === undefined
      ? null
      : clientCallName({
          preferredName: visitClient.preferred_name ?? null,
          firstName: visitClient.first_name ?? null,
          displayName: visitClient.display_name,
        });
  const attention = home?.attention ?? [];
  const rail = home?.rail ?? [];
  // Empty wherever my_week is null (the wire's own gate, cmHome.ts).
  const spiceAttention = home?.spice_attention ?? [];
  // A SPICE-only viewer's Home speaks SPICE, never care management. This
  // is a whoami fact (isSpiceOnlyViewer), NOT the same thing as "my_week
  // is present": a dual-role viewer (does_spice AND does_care_management)
  // also carries my_week — for the client she covers as therapist — but
  // keeps the ordinary care-manager home below, since she is not
  // SPICE-only.
  const spiceOnly = isSpiceOnlyViewer(whoami);
  // The search's wider scope (founder walk): caseload first, then the
  // unit's other clients, then (for a viewer who reads every client
  // without view_workspace) every other roster client. The pool is
  // de-duplicated by id; every entry carries names/navigation only.
  const unitClients = home?.unit_clients ?? [];
  // The hero's purpose line: the date, then the day's visits (a care
  // manager's appointments; a SPICE-only viewer's planned sessions; and
  // either one's own planned care visits).
  // Until the snapshot lands there is nothing to count, so the line is
  // the date alone.
  // LP-11: the counts line, "Tuesday, October 20 · 14 clients · 2 visits ·
  // 1 task overdue" (the overdue count only for a seat with a task list).
  const countsParts = [orientationDate(todayIso)];
  if (home !== null) {
    countsParts.push(countPhrase(clients.length, 'client', 'clients'));
    countsParts.push(countPhrase(visitsTodayCount(home, todayIso), 'visit', 'visits'));
  }
  if (canActOnTasks && tasksQ.rows !== undefined && !tasksQ.isLoading) {
    const overdue = tasksQ.rows.filter((t) => t.status !== 'done' && isOverdue(t)).length;
    countsParts.push(`${countPhrase(overdue, 'task', 'tasks')} overdue`);
  }
  const purposeLine = countsParts.join(' · ');

  const q = search.trim().toLowerCase();
  const searchMatches: { id: string; display_name: string }[] = [];
  if (q.length >= 2) {
    const seen = new Set<string>();
    for (const c of [...clients, ...unitClients, ...moreClients]) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      if (c.display_name.toLowerCase().includes(q)) searchMatches.push(c);
      if (searchMatches.length === 6) break;
    }
  }

  return (
    <PageShell
      maxWidth="bleed"
      access={{ allowed: allowed || canActOnTasks }}
      async={{ what: 'your clients', isLoading, isError, error, onRetry: refetch }}
      testId="cm-home"
    >
      {/* The band is the FULL-BLEED SectionBand primitive like every other
          page (the inset rounded card retired). SectionBand carries the
          codified watermark clip layer — the search dropdown still escapes
          the band. Header shape (the daily-page pattern): the TITLE is the
          page's identity ("Your day"); the quiet purpose line under it is
          the date (injected today, never Date.now()) and the day's visits. */}
      <SectionBand
        testId="client-context-band"
        width="wide"
        eyebrow="Clients"
        title={MY_CLIENTS_HEADINGS.page}
        purpose={purposeLine}
        purposeTestId="my-clients-counts"
        search={
          <div className="relative w-60 md:w-72">
            <SearchField
              ref={searchRef}
              value={search}
              onChange={setSearch}
              placeholder="Find a client"
              aria-label="Find a client"
              size="compact"
              testId="home-search"
            />
            {searchMatches.length > 0 && (
              <ul
                data-testid="home-search-results"
                className="absolute z-30 mt-1 w-full list-none rounded-card border border-sage-200 bg-paper p-1 shadow-sh2"
              >
                {searchMatches.map((c) => (
                  <li key={c.id} className="px-2 py-1.5">
                    <NavArrowLink to={`/t/${tenantSlug}/clients/${c.id}`}>
                      {c.display_name}
                    </NavArrowLink>
                  </li>
                ))}
              </ul>
            )}
          </div>
        }
        action={
          /* The ONE primary action, hero size (R1) — primary-on-dark on the
             forest band (#446: cream fill, forest text; the protected hero
             treatment, inverted for the dark band). Hidden for a read-only
             viewer (0152) — a write-intent CTA. A SPICE-only viewer's Home
             speaks SPICE: the door opens the Email helper in SPICE mode
             with no client picked, not the care-management case-note
             picker — and the SPICE accent role means this button itself
             takes pepper instead of the on-dark forest treatment. */
          canWriteClinical || canLogTime ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {canWriteClinical && (
                <Button
                  variant={spiceOnly ? 'pepper' : 'primary-on-dark'}
                  size="md"
                  className="px-[18px] py-2 text-sm"
                  data-testid="hero-case-note"
                  onClick={() =>
                    spiceOnly
                      ? navigate(`/t/${tenantSlug}/ai-tools/email?mode=spice`)
                      : setPickerOpen(true)
                  }
                >
                  {spiceOnly ? 'Write the SPICE email summary' : '+ Case Note'}
                </Button>
              )}
              {/* The door to internal time: LP-43, the Log internal time
                  dialog itself, here, not a trip to My time. */}
              {/* The owner's look on staging: as visible as + Case Note, the
                  same filled cream button, and My time's own words. */}
              {canLogTime && (
                <Button
                  variant="primary-on-dark"
                  size="md"
                  className="px-[18px] py-2 text-sm"
                  data-testid="hero-admin-time"
                  loading={logOpen && !logReady}
                  onClick={openLog}
                >
                  + Internal Time
                </Button>
              )}
              {canLogTime && logFailed && (
                <p role="alert" data-testid="hero-admin-time-error" className="basis-full text-right text-xs text-cream">
                  {pageReadText(hoursQ.error, supportContact, 'your pay period')}
                </p>
              )}
            </div>
          ) : undefined
        }
      />

      {/* LP-11 My Clients: two columns on wide screens, Coming Up and the
          client list on the left and a rail sticky under the header on the
          right. On a phone it is one column in document order: coming up,
          clients, notes to confirm, tasks, team. LP-24: the rail is 400px
          at lg and two fifths of the page from xl; every section is its own
          white card. */}
      <div
        data-testid="cm-home-body"
        className={`${PAGE_WIDTH_CLASSES.wide} mx-auto grid items-start gap-8 ${PAGE_GUTTER} py-6 lg:grid-cols-[minmax(0,1fr)_400px] xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]`}
      >
        {allowed ? (
          // The first card on each side shares one top line.
          <div className="min-w-0 space-y-6">
            {/* LP-24: Coming Up heads the column. */}
            <ComingUpList home={home} todayIso={todayIso} tenantSlug={tenantSlug} canWriteClinical={canWriteClinical} />
            {/* My Week, a SPICE viewer's working schedule (Edit schedule and
                not this week intact), heads the column for every viewer who
                does SPICE; the read carries it only for them. */}
            {home?.my_week && (
              <MyWeekBand
                myWeek={home.my_week}
                todayIso={todayIso}
                tenantSlug={tenantSlug}
                canWriteClinical={canWriteClinical}
                canEditSchedule={canEditSchedule}
                showSpiceWeekGridLink={showSpiceWeekGridLink}
                showAllSpiceClientsLink={showAllClientsLink}
              />
            )}
            <MyClientsList
              clients={clients}
              todayIso={todayIso}
              tenantSlug={tenantSlug}
              showAllClientsLink={showAllClientsLink}
              canWriteClinical={canWriteClinical}
              spiceOnlyViewer={spiceOnly}
              onVisit={canPlanVisits ? setVisitClientId : undefined}
            />
          </div>
        ) : (
          <div />
        )}
        <aside
          aria-label="Today and your work"
          data-testid="my-clients-rail"
          className="min-w-0 space-y-6 lg:sticky lg:top-6 lg:-mx-1 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto lg:px-1 lg:pb-1"
        >
          {allowed && (
            <AttentionBand
              attention={attention}
              spiceAttention={spiceAttention}
              todayIso={todayIso}
              tenantSlug={tenantSlug}
              canWriteClinical={canWriteClinical}
            />
          )}
          {canActOnTasks && (
            <section id="your-day-tasks" data-testid="your-day-tasks" aria-labelledby="your-day-tasks-heading" className="min-w-0">
              {/* LP-24: the list renders the heading so the office's
                  Filter shares its line. */}
              <HubMyTasks
                embedded
                rail
                heading={<h2 id="your-day-tasks-heading" className="flex h-7 items-center font-display text-lg leading-7 text-sage-800">{MY_CLIENTS_HEADINGS.myTasks}</h2>}
                caseload={clients.map((c) => ({ id: c.id, display_name: c.display_name, pronouns: c.pronouns }))}
              />
            </section>
          )}
          {allowed && <TeamRail rail={rail} todayIso={todayIso} />}
        </aside>
      </div>

      {selectedItemId && <WorkItemDrawer key={selectedItemId} itemId={selectedItemId} onClose={closeItem} />}

      {visitClientId !== null && (
        <VisitOrAppointmentDialog
          clientId={visitClientId}
          clientName={visitClientName}
          face="visit"
          onClose={() => setVisitClientId(null)}
        />
      )}

      {logOpen && logReady && hoursQ.data !== null && (
        <LogInternalTimeDialog
          tenantSlug={tenantSlug}
          period={hoursQ.data.period}
          configPresent={hoursQ.data.config_present}
          hoursIncrement={readHoursIncrement(tenantConfigQ.data?.config)}
          todayIso={todayIso}
          onClose={() => setLogOpen(false)}
          onSaved={() => setLogged(true)}
        />
      )}
      {/* LP-43: the save is confirmed in a popup she
          can read, not a line under the buttons. She has not left the page. */}
      {canLogTime && logged && (
        <Dialog
          title="Your internal time was saved"
          size="sm"
          testId="hero-admin-time-logged"
          onClose={() => setLogged(false)}
          footer={
            <>
              <Link
                to={`/t/${tenantSlug}/my-time`}
                className={buttonClassName('secondary', 'md')}
                data-testid="hero-admin-time-logged-open"
                onClick={() => setLogged(false)}
              >
                Open My time
              </Link>
              <Button variant="primary" size="md" data-testid="hero-admin-time-logged-done" onClick={() => setLogged(false)}>
                Done
              </Button>
            </>
          }
        >
          {/* The title says it all; the two ways on are the buttons. */}
          {null}
        </Dialog>
      )}

      {pickerOpen && (
        <CaseNotePicker
          clients={clients}
          moreClients={usesEveryClient ? moreClients : undefined}
          tenantSlug={tenantSlug}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </PageShell>
  );
}
